import { useCallback, useEffect, useRef, useState } from "react";

type VideoPlayerProps = {
  src: string;
  poster?: string;
  title?: string;
  className?: string;
};

const SPEEDS = [0.75, 1, 1.25, 1.5, 2] as const;
const VOLUME_STORAGE_KEY = "sunsmart:video:volume";

function formatTime(seconds: number): string {
  if (!Number.isFinite(seconds) || seconds < 0) return "0:00";
  const total = Math.floor(seconds);
  const m = Math.floor(total / 60);
  const s = total % 60;
  const h = Math.floor(m / 60);
  const mm = h > 0 ? String(m % 60).padStart(2, "0") : String(m);
  const ss = String(s).padStart(2, "0");
  return h > 0 ? `${h}:${mm}:${ss}` : `${mm}:${ss}`;
}

export function VideoPlayer({ src, poster, title, className = "" }: VideoPlayerProps) {
  const videoRef = useRef<HTMLVideoElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const hideControlsTimer = useRef<number | null>(null);

  const [isPlaying, setIsPlaying] = useState(false);
  const [hasStarted, setHasStarted] = useState(false);
  const [currentTime, setCurrentTime] = useState(0);
  const [duration, setDuration] = useState(0);
  const [buffered, setBuffered] = useState(0);
  const [volume, setVolume] = useState(1);
  const [muted, setMuted] = useState(false);
  const [speed, setSpeed] = useState<number>(1);
  const [showSpeedMenu, setShowSpeedMenu] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [controlsVisible, setControlsVisible] = useState(true);

  // Restore saved volume on mount (client only)
  useEffect(() => {
    try {
      const saved = window.localStorage.getItem(VOLUME_STORAGE_KEY);
      if (saved !== null) {
        const v = Math.min(1, Math.max(0, parseFloat(saved)));
        if (!Number.isNaN(v)) {
          setVolume(v);
          if (videoRef.current) videoRef.current.volume = v;
        }
      }
    } catch {
      /* ignore */
    }
  }, []);

  const scheduleHide = useCallback(() => {
    if (hideControlsTimer.current) window.clearTimeout(hideControlsTimer.current);
    hideControlsTimer.current = window.setTimeout(() => {
      if (videoRef.current && !videoRef.current.paused) setControlsVisible(false);
    }, 2200);
  }, []);

  const showControls = useCallback(() => {
    setControlsVisible(true);
    scheduleHide();
  }, [scheduleHide]);

  useEffect(() => () => {
    if (hideControlsTimer.current) window.clearTimeout(hideControlsTimer.current);
  }, []);

  const togglePlay = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    if (v.paused) {
      v.play().catch(() => {});
    } else {
      v.pause();
    }
  }, []);

  const seekBy = useCallback((delta: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.min(v.duration || 0, Math.max(0, v.currentTime + delta));
    showControls();
  }, [showControls]);

  const seekTo = useCallback((time: number) => {
    const v = videoRef.current;
    if (!v) return;
    v.currentTime = Math.min(v.duration || 0, Math.max(0, time));
  }, []);

  const changeVolume = useCallback((v: number) => {
    const clamped = Math.min(1, Math.max(0, v));
    setVolume(clamped);
    setMuted(clamped === 0);
    if (videoRef.current) {
      videoRef.current.volume = clamped;
      videoRef.current.muted = clamped === 0;
    }
    try {
      window.localStorage.setItem(VOLUME_STORAGE_KEY, String(clamped));
    } catch {
      /* ignore */
    }
  }, []);

  const toggleMute = useCallback(() => {
    const v = videoRef.current;
    if (!v) return;
    const next = !v.muted;
    v.muted = next;
    setMuted(next);
  }, []);

  const applySpeed = useCallback((s: number) => {
    setSpeed(s);
    setShowSpeedMenu(false);
    if (videoRef.current) videoRef.current.playbackRate = s;
  }, []);

  const toggleFullscreen = useCallback(async () => {
    const el = containerRef.current;
    if (!el) return;
    if (document.fullscreenElement) {
      await document.exitFullscreen().catch(() => {});
    } else {
      await el.requestFullscreen().catch(() => {});
    }
  }, []);

  useEffect(() => {
    const onFsChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", onFsChange);
    return () => document.removeEventListener("fullscreenchange", onFsChange);
  }, []);

  // Keyboard shortcuts when focused
  const onKeyDown = useCallback((e: React.KeyboardEvent) => {
    const key = e.key;
    if (key === " " || key === "k") {
      e.preventDefault();
      togglePlay();
    } else if (key === "ArrowLeft") {
      e.preventDefault();
      seekBy(-5);
    } else if (key === "ArrowRight") {
      e.preventDefault();
      seekBy(5);
    } else if (key === "ArrowUp") {
      e.preventDefault();
      changeVolume(volume + 0.1);
    } else if (key === "ArrowDown") {
      e.preventDefault();
      changeVolume(volume - 0.1);
    } else if (key === "f") {
      e.preventDefault();
      toggleFullscreen();
    } else if (key === "m") {
      e.preventDefault();
      toggleMute();
    } else if (/^[0-9]$/.test(key)) {
      e.preventDefault();
      const pct = parseInt(key, 10) / 10;
      seekTo((duration || 0) * pct);
    }
  }, [togglePlay, seekBy, changeVolume, volume, toggleFullscreen, toggleMute, seekTo, duration]);

  // Scrubber interaction
  const scrubbing = useRef(false);
  const scrubberRef = useRef<HTMLDivElement>(null);
  const handleScrub = (clientX: number) => {
    const bar = scrubberRef.current;
    const v = videoRef.current;
    if (!bar || !v || !v.duration) return;
    const rect = bar.getBoundingClientRect();
    const ratio = Math.min(1, Math.max(0, (clientX - rect.left) / rect.width));
    v.currentTime = ratio * v.duration;
  };
  const onScrubberDown = (e: React.PointerEvent) => {
    scrubbing.current = true;
    (e.target as Element).setPointerCapture?.(e.pointerId);
    handleScrub(e.clientX);
  };
  const onScrubberMove = (e: React.PointerEvent) => {
    if (!scrubbing.current) return;
    handleScrub(e.clientX);
  };
  const onScrubberUp = (e: React.PointerEvent) => {
    scrubbing.current = false;
    (e.target as Element).releasePointerCapture?.(e.pointerId);
  };

  const progressPct = duration > 0 ? (currentTime / duration) * 100 : 0;
  const bufferedPct = duration > 0 ? (buffered / duration) * 100 : 0;

  return (
    <div
      ref={containerRef}
      className={`group relative overflow-hidden rounded-sm border border-border bg-black ${className}`}
      onPointerMove={showControls}
      onPointerLeave={() => {
        if (videoRef.current && !videoRef.current.paused) setControlsVisible(false);
      }}
      onKeyDown={onKeyDown}
      tabIndex={0}
    >
      <video
        ref={videoRef}
        src={src}
        poster={poster}
        title={title}
        playsInline
        preload="metadata"
        className="block aspect-video w-full bg-black"
        onClick={togglePlay}
        onPlay={() => {
          setIsPlaying(true);
          setHasStarted(true);
          scheduleHide();
        }}
        onPause={() => {
          setIsPlaying(false);
          setControlsVisible(true);
        }}
        onTimeUpdate={(e) => setCurrentTime(e.currentTarget.currentTime)}
        onLoadedMetadata={(e) => setDuration(e.currentTarget.duration || 0)}
        onProgress={(e) => {
          const v = e.currentTarget;
          if (v.buffered.length > 0) {
            setBuffered(v.buffered.end(v.buffered.length - 1));
          }
        }}
        onVolumeChange={(e) => {
          setVolume(e.currentTarget.volume);
          setMuted(e.currentTarget.muted);
        }}
      />

      {/* Big center play button when paused / not started */}
      {!isPlaying && (
        <button
          type="button"
          aria-label="Play video"
          onClick={togglePlay}
          className="absolute inset-0 flex items-center justify-center bg-black/20 transition-colors hover:bg-black/30"
        >
          <span className="flex h-20 w-20 items-center justify-center rounded-full border-2 border-sun bg-background/80 text-sun shadow-lg backdrop-blur-sm transition-transform group-hover:scale-105 md:h-24 md:w-24">
            <svg viewBox="0 0 24 24" fill="currentColor" className="ml-1 h-8 w-8 md:h-10 md:w-10" aria-hidden="true">
              <path d="M8 5v14l11-7z" />
            </svg>
          </span>
        </button>
      )}

      {/* Controls */}
      <div
        className={`pointer-events-none absolute inset-x-0 bottom-0 bg-gradient-to-t from-black/85 via-black/50 to-transparent px-3 pb-3 pt-10 transition-opacity duration-300 md:px-4 md:pb-4 ${
          controlsVisible || !isPlaying ? "opacity-100" : "opacity-0"
        }`}
      >
        <div className="pointer-events-auto flex flex-col gap-2">
          {/* Scrubber */}
          <div
            ref={scrubberRef}
            role="slider"
            aria-label="Seek"
            aria-valuemin={0}
            aria-valuemax={Math.max(0, Math.floor(duration))}
            aria-valuenow={Math.floor(currentTime)}
            tabIndex={0}
            onPointerDown={onScrubberDown}
            onPointerMove={onScrubberMove}
            onPointerUp={onScrubberUp}
            className="relative h-1.5 w-full cursor-pointer rounded-full bg-white/20 transition-[height] hover:h-2"
          >
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-white/30"
              style={{ width: `${bufferedPct}%` }}
            />
            <div
              className="absolute inset-y-0 left-0 rounded-full bg-sun"
              style={{ width: `${progressPct}%` }}
            />
            <div
              className="absolute top-1/2 h-3 w-3 -translate-x-1/2 -translate-y-1/2 rounded-full bg-sun opacity-0 shadow group-hover:opacity-100"
              style={{ left: `${progressPct}%` }}
            />
          </div>

          {/* Buttons row */}
          <div className="flex items-center gap-2 text-white md:gap-3">
            <button
              type="button"
              onClick={togglePlay}
              aria-label={isPlaying ? "Pause" : "Play"}
              className="flex h-9 w-9 items-center justify-center rounded-sm text-white hover:text-sun"
            >
              {isPlaying ? (
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                  <path d="M6 5h4v14H6zM14 5h4v14h-4z" />
                </svg>
              ) : (
                <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                  <path d="M8 5v14l11-7z" />
                </svg>
              )}
            </button>

            {/* Volume */}
            <div className="group/vol flex items-center">
              <button
                type="button"
                onClick={toggleMute}
                aria-label={muted || volume === 0 ? "Unmute" : "Mute"}
                className="flex h-9 w-9 items-center justify-center rounded-sm hover:text-sun"
              >
                {muted || volume === 0 ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.59 3L20 8.41 18.59 7l-3.59 3.59L11.41 7 10 8.41 13.59 12 10 15.59 11.41 17l3.59-3.59L18.59 17 20 15.59z" />
                  </svg>
                ) : volume < 0.5 ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                    <path d="M7 9v6h4l5 5V4l-5 5H7zm11.5 3c0-1.77-1-3.29-2.5-4.03v8.05c1.5-.73 2.5-2.25 2.5-4.02z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                    <path d="M3 9v6h4l5 5V4L7 9H3zm13.5 3c0-1.77-1-3.29-2.5-4.03v8.05c1.5-.73 2.5-2.25 2.5-4.02zM14 3.23v2.06c2.89.86 5 3.54 5 6.71s-2.11 5.85-5 6.71v2.06c4.01-.91 7-4.49 7-8.77s-2.99-7.86-7-8.77z" />
                  </svg>
                )}
              </button>
              <input
                type="range"
                min={0}
                max={1}
                step={0.05}
                value={muted ? 0 : volume}
                onChange={(e) => changeVolume(parseFloat(e.target.value))}
                aria-label="Volume"
                className="ml-1 hidden h-1 w-20 cursor-pointer appearance-none rounded-full bg-white/25 accent-sun md:block"
              />
            </div>

            <div className="ml-1 whitespace-nowrap font-mono text-xs text-white/85 md:text-sm">
              {formatTime(currentTime)} <span className="text-white/50">/</span> {formatTime(duration)}
            </div>

            <div className="ml-auto flex items-center gap-1">
              {/* Speed */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowSpeedMenu((v) => !v)}
                  aria-label="Playback speed"
                  aria-expanded={showSpeedMenu}
                  className="h-9 rounded-sm px-2 font-mono text-xs uppercase tracking-wide hover:text-sun md:text-sm"
                >
                  {speed}x
                </button>
                {showSpeedMenu && (
                  <div
                    role="menu"
                    className="absolute bottom-full right-0 mb-2 min-w-[80px] overflow-hidden rounded-sm border border-border bg-background text-foreground shadow-lg"
                  >
                    {SPEEDS.map((s) => (
                      <button
                        key={s}
                        type="button"
                        role="menuitemradio"
                        aria-checked={speed === s}
                        onClick={() => applySpeed(s)}
                        className={`block w-full px-3 py-1.5 text-left font-mono text-xs hover:bg-surface ${
                          speed === s ? "text-sun" : ""
                        }`}
                      >
                        {s}x
                      </button>
                    ))}
                  </div>
                )}
              </div>

              {/* Fullscreen */}
              <button
                type="button"
                onClick={toggleFullscreen}
                aria-label={isFullscreen ? "Exit fullscreen" : "Enter fullscreen"}
                className="flex h-9 w-9 items-center justify-center rounded-sm hover:text-sun"
              >
                {isFullscreen ? (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                    <path d="M5 16h3v3h2v-5H5v2zm3-8H5v2h5V5H8v3zm6 11h2v-3h3v-2h-5v5zm2-11V5h-2v5h5V8h-3z" />
                  </svg>
                ) : (
                  <svg viewBox="0 0 24 24" fill="currentColor" className="h-5 w-5" aria-hidden="true">
                    <path d="M7 14H5v5h5v-2H7v-3zm-2-4h2V7h3V5H5v5zm12 7h-3v2h5v-5h-2v3zM14 5v2h3v3h2V5h-5z" />
                  </svg>
                )}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}

export default VideoPlayer;
